import { styled } from '@mui/material/styles';
import { FormControlLabel } from '@mui/material';
import TimeframePicker from '@components/TimeframePicker';
import { Switch } from '@components/Switch';
import { Typography } from '@components/Typography';
import FieldWithActions, { AddRowButton, rowDeleteAction } from '@components/FieldWithActions';
import HoursIntervals from '@features/HoursIntervals';
import { DEFAULT_HOURS_INTERVAL } from '@constants/companies';
import { useTranslation } from '@hooks';

const List = styled('div')({
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
});

const Row = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: '40px',
    padding: '4px',
    gap: '8px',
    '& .MuiFormControl-root': {
        paddingBottom: 0,
    },
    '@media (max-width: 600px)': {
        flexWrap: 'wrap',
    },
});

const DayToggle = styled(FieldWithActions)({
    '&&': {
        width: 'auto',
    },
});

const StyledTimeframePicker = styled(TimeframePicker)({
    maxWidth: 180,
    '& input': {
        paddingRight: '4px !important',
    },
    '@media (max-width: 600px)': {
        width: 150,
    },
});

const SpecialHoursSelection = ({ data, onChange, canAdd = true }) => {
    const { t } = useTranslation();

    const updateDay = (dayIndex, patch) =>
        onChange(data.map((day, index) => (index === dayIndex ? { ...day, ...patch } : day)));

    const removeDay = dayIndex => onChange(data.filter((_, index) => index !== dayIndex));

    const changeHours = (dayIndex, hours) => (hours.length > 0 ? updateDay(dayIndex, { hours }) : removeDay(dayIndex));

    const addDay = () =>
        onChange([...data, { date: new Date(), isClosed: true, hours: [{ ...DEFAULT_HOURS_INTERVAL }] }]);

    const openSwitch = (day, dayIndex) => (
        <FormControlLabel
            sx={{ height: '40px', margin: 0 }}
            control={
                <Switch
                    sx={{ margin: 0 }}
                    checked={!day.isClosed}
                    handleChange={e => updateDay(dayIndex, { isClosed: !e.target.checked })}
                />
            }
            label={
                <Typography variant="body2" color="textPrimary">
                    {day.isClosed ? t('common.closed') : t('common.open')}
                </Typography>
            }
        />
    );

    return (
        <List>
            {data.map((currentDay, dayIndex) => (
                <Row key={dayIndex}>
                    <StyledTimeframePicker
                        value={[currentDay.date]}
                        range={false}
                        onChangeTimeframe={value => updateDay(dayIndex, { date: value })}
                    />
                    <DayToggle
                        actions={currentDay.isClosed ? rowDeleteAction({ onDelete: () => removeDay(dayIndex), t }) : []}
                    >
                        {openSwitch(currentDay, dayIndex)}
                    </DayToggle>
                    {!currentDay.isClosed && (
                        <HoursIntervals
                            hours={currentDay.hours}
                            onChange={hours => changeHours(dayIndex, hours)}
                            canEndAtMidnight
                            canRemoveAll
                        />
                    )}
                </Row>
            ))}
            {canAdd && <AddRowButton label={t('buttons.addNewDate')} onClick={addDay} />}
        </List>
    );
};

export default SpecialHoursSelection;
